import NumberInput from "../../../components/NumberInput";
import React, { useMemo, useRef } from "react";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";
import AkasSelect from "../../../components/AkasSelect";

// Keep the client contract aligned with UnitController rules.

const FormUnit = (props) => {
  const { isEdit, closeModal, formState, setFormState, units, isLoading, handleAdd, handleUpdate } = props;
  const formRef = useRef(null);
  const validationSchema = useMemo(() => ({
    value: [rule.required("Kode unit wajib diisi."), rule.maxLength(50)],
    label: [rule.required("Label unit wajib diisi."), rule.maxLength(50)],
    name: [rule.required("Nama unit wajib diisi."), rule.maxLength(100)],
    base_unit_id: rule.custom((value) => String(value || "") === String(formState.id || "") ? "Unit tidak dapat menjadi unit dasar untuk dirinya sendiri." : ""),
    factor: [rule.number(), rule.greaterThan(0, "Faktor konversi harus lebih dari 0.")],
    description: rule.maxLength(255),
  }), [formState.id]);
  const validation = useFormValidation({ values: formState, schema: validationSchema, formId: "unit", focusRoot: formRef });

  const updateField = (field, value) => {
    setFormState((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;
    const success = isEdit ? await handleUpdate() : await handleAdd();

    if (success !== false) {
      document.querySelector("#modal-form .btn-close")?.click();
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-form"
      tabIndex="-1"
      role="dialog"
      aria-hidden="false"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <form ref={formRef} className="modal-content" onSubmit={handleSubmit} noValidate>
          <div className="modal-header">
            <h5 className="modal-title">Master Unit {!isEdit ? "Baru" : "Edit"}</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={closeModal}></button>
          </div>
          <div className="modal-body">
            <FormValidationNotice errors={validation.errors} />
            <div className="row">
              <div className="col-lg-4">
                <div className="mb-3">
                  <label className="form-label">Kode Unit<RequiredMark /></label>
                  <input
                    {...validation.fieldProps("value")}
                    type="text"
                    className={validation.inputClass("value")}
                    placeholder="contoh: pcs"
                    value={formState.value}
                    maxLength={50}
                    required
                    onChange={(event) => updateField("value", event.target.value.toLowerCase().replace(/\s+/g, "_"))}
                  />
                  <FieldFeedback formId="unit" name="value" error={validation.errors.value} hint="Maksimal 50 karakter, tanpa spasi." />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="mb-3">
                  <label className="form-label">Label<RequiredMark /></label>
                  <input
                    {...validation.fieldProps("label")}
                    type="text"
                    className={validation.inputClass("label")}
                    placeholder="contoh: Pcs"
                    value={formState.label}
                    maxLength={50}
                    required
                    onChange={(event) => updateField("label", event.target.value)}
                  />
                  <FieldFeedback formId="unit" name="label" error={validation.errors.label} hint="Nama singkat yang tampil, misalnya Kg atau Liter." />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="mb-3">
                  <label className="form-label">Nama Unit<RequiredMark /></label>
                  <input
                    {...validation.fieldProps("name")}
                    type="text"
                    className={validation.inputClass("name")}
                    placeholder="contoh: Piece"
                    value={formState.name}
                    maxLength={100}
                    required
                    onChange={(event) => updateField("name", event.target.value)}
                  />
                  <FieldFeedback formId="unit" name="name" error={validation.errors.name} hint="Maksimal 100 karakter." />
                </div>
              </div>
              <div className="col-lg-8">
                <div className="mb-3">
                  <label className="form-label">Unit Dasar<RequiredMark optional /></label>
                  <AkasSelect
                    {...validation.fieldProps("base_unit_id")}
                    data-validation-field="base_unit_id"
                    className={validation.inputClass("base_unit_id", "form-select")}
                    value={formState.base_unit_id || ""}
                    onChange={(event) => updateField("base_unit_id", event.target.value || null)}>
                    <option value="">-- Tidak Ada Unit Dasar --</option>
                    {units
                      .filter((unit) => unit.id !== formState.id)
                      .map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.label} - {unit.name}
                        </option>
                      ))}
                  </AkasSelect>
                  <FieldFeedback formId="unit" name="base_unit_id" error={validation.errors.base_unit_id} hint="Kosongkan bila unit ini merupakan satuan dasar." />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="mb-3">
                  <label className="form-label">Faktor Konversi<RequiredMark /></label>
                  <NumberInput
                    {...validation.fieldProps("factor")}
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    className={validation.inputClass("factor")}
                    value={formState.factor}
                    required
                    onChange={(event) => updateField("factor", event.target.value)}
                  />
                  <FieldFeedback formId="unit" name="factor" error={validation.errors.factor} hint="Harus lebih dari 0. Contoh: 1 kg = 1.000 gram." />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="form-label">Deskripsi<RequiredMark optional /></label>
                  <textarea
                    {...validation.fieldProps("description")}
                    className={validation.inputClass("description")}
                    rows="3"
                    placeholder="keterangan penggunaan unit"
                    value={formState.description}
                    maxLength={255}
                    onChange={(event) => updateField("description", event.target.value)}></textarea>
                  <FieldFeedback formId="unit" name="description" error={validation.errors.description} hint="Maksimal 255 karakter." />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">
              Cancel
            </button>
            {isEdit ? (
              <button type="submit" className="btn btn-primary btn-5 ms-auto" disabled={isLoading}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-tabler icons-tabler-outline icon-tabler-edit">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                  <path d="M16 5l3 3" />
                </svg>
                {isLoading ? "Menyimpan..." : "Edit Data"}
              </button>
            ) : (
              <button type="submit" className="btn btn-primary btn-5 ms-auto" disabled={isLoading}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-2">
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
                {isLoading ? "Menyimpan..." : "Tambah Data"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormUnit;
